import {
  IPrinterConfig,
  ISettings,
  IPicklistGenerator$IAuto,
  ILdapTestRequest,
  IUpdateTranslations,
  ILicense,
  IIErpDescriptor$IErpTestResults,
  Role,
  ITestMail,
  ISystemScriptEntity,
  ITestScriptSource,
  ITabulaController$ICompanion$IPdfCoord,
  IRegisteredServiceInfo,
  IAnalyticsModelDefinition,
  IScriptingSecret,
  ITaskOverview,
  IScriptedTask,
  IIpFilterTestRequest,
  IpFilterRule,
  IGetContentsDto,
  IGetContentsResponseEntryDto,
  IMoveObjectDto,
  IGetFileDto,
  IGetFileResponseDto,
  IDeleteObjectDto,
  IUploadFileDto,
  ICreateDirectoryDto,
  IReportDefinition,
  INativeLibrarySettings,
  IBackupEntry,
  IDeploymentConfig,
  IUserFunctionDefinition,
  ILabelSpec,
  ILabelTemplate,
  ILabelInput,
  ISystemHealthChecker$ISystemHealthMessage,
  IProxyCall,
  IProxyCallResult,
  IScriptingUserLibrary,
  IAlertDefinition,
  IAlertRecord,
  ISystemEvent,
  IExternalServiceRegistration,
  IUserApiDefinition,
  IUserTask,
  IUserTaskTemplate,
  IDoc2Zpl,
  ITaskConfiguration,
  IKanbanBoardEntity,
  IKanbanColumnEntity,
  IKanbanCardEntity,
  ICustomizerFile,
  ICustomizerController$ICompanion$IFrontendBundle,
  IReportSettings,
  IQzTraySettings,
  IEmailerDefinition,
  IEmailerController$IEmailerTarget,
  IDocumentLayout,
  IEmailerController$ITestEmail,
  IIWhsPerformanceDao$ISchema
} from 'interfaces';
import { Registry } from '.';
import { saveBase64UrlEncode } from 'component_utils/utils';
import { IAlertDto } from 'interfaces';

export type RawRoleHierarchy = Map<Role, Array<Role>>;

export type TabulaResults = {
  data: {
    text: string;
  }[][];
};

export default ({ get, post, destroy }: Registry) => {
  return {
    saveSystemConfiguration: (newSettings: any): Promise<void> => post('/api/v1/admin/save_system_config', newSettings),
    testErpConfiguration: (newSettings: any): Promise<IIErpDescriptor$IErpTestResults> =>
      post('/api/v1/admin/test_erp_connection', newSettings),
    autoFixErpErrors: (newSettings: any): Promise<void> => post('/api/v1/admin/autoFixErpTests', newSettings),
    getSystemConfiguration: (): Promise<ISettings> => get('/api/v1/admin/get_system_config'),

    directQuery: (q: string): Promise<any[]> => post('/api/v1/system/directQuery', { q }),

    getCustomGridForObjectInformation: (object: string, idx: number, code: string) => get<any[]>(
      `/api/v1/system/information_custom_grid/${object}/${idx}/${saveBase64UrlEncode(code)}`
    ),

    systemHealth: () => get<ISystemHealthChecker$ISystemHealthMessage[]>('/api/v1/system/systemHealth'),

    events: {
      getRetryables: () => get<string[]>('/api/v1/system_events/retryable'),
      getUnsolvedEvents: () => get<ISystemEvent[]>('/api/v1/system_events/'),
      retry: (id: number) => post(`/api/v1/system_events/${id}/retry`, {}),
      markResolved: (id: number) => post(`/api/v1/system_events/${id}/markSolved`, {}),
      markAllResolved: () => post('/api/v1/system_events/markAllSolved', {})
    },

    notifications: {
      increase: (identifier: string): Promise<void> => post('/api/v1/notifications/increase', { identifier }),
      decrease: (identifier: string): Promise<void> => post('/api/v1/notifications/decrease', { identifier }),
    },

    fileManagement: {
      getContents: (payload: IGetContentsDto, withContents: boolean): Promise<IGetContentsResponseEntryDto[]> => post(`/api/v1/fileManagement/contents/${withContents}`, payload),
      createDirectory: (payload: ICreateDirectoryDto): Promise<ICreateDirectoryDto[]> => post('/api/v1/fileManagement/createDirectory', payload),
      uploadFile: (payload: IUploadFileDto): Promise<void> => post('/api/v1/fileManagement/uploadFile', payload),
      deleteObject: (payload: IDeleteObjectDto): Promise<void> => post('/api/v1/fileManagement/deleteObject', payload),
      getFile: (payload: IGetFileDto): Promise<IGetFileResponseDto> => post('/api/v1/fileManagement/getFile', payload),
      moveObject: (payload: IMoveObjectDto): Promise<void> => post('/api/v1/fileManagement/moveObject', payload),
      copyObject: (payload: IMoveObjectDto): Promise<void> => post('/api/v1/fileManagement/copyObject', payload),
    },

    deployment: {
      getBackups: (): Promise<IBackupEntry[]> => get('/api/v1/system_maintenance/get_backups'),
      deleteBackup: (id: string): Promise<IBackupEntry[]> => destroy(`/api/v1/system_maintenance/${saveBase64UrlEncode(id)}`),

      // these methods will take the application down
      installNewVersion: (app: File, config: IDeploymentConfig, uploadProgress: (percentage: number) => any): Promise<void> => {
        const formData = new FormData();
        formData.append('file', app);
        formData.append('config', new Blob([JSON.stringify(config)], { type: 'application/json' }));
        return post(
          '/api/v1/system_maintenance/install_new', 
          formData, 
          undefined, 
          {
            expectNetworkError: true
          },
          (e) => {
            console.log(e)
            if (e.total) {
              const percent = Math.round((e.loaded * 100) / e.total);
              uploadProgress(percent)
            }
          }
        );
      },
      makeBackup: (config: IDeploymentConfig): Promise<void> => post('/api/v1/system_maintenance/make_backup', config, undefined, { expectNetworkError: true }),
      rollback: (id: string, config: IDeploymentConfig): Promise<IBackupEntry[]> => post(`/api/v1/system_maintenance/${saveBase64UrlEncode(id)}/rollback`, config, undefined, { expectNetworkError: true }),
    },

    license: {
      getLicense: (): Promise<ILicense> => get('/api/v1/license'),
      saveLicense: (license: File): Promise<void> => {
        const formData = new FormData();
        formData.append('file', license);
        return post('/api/v1/license', formData);
      },
    },

    certificates: {
      getCertificateState: () => get('/api/v1/certificates'),
      storeCertificate: (cert: File, config: any) => {
        const formData = new FormData();
        formData.append('cert', cert);
        formData.append('config', new Blob([JSON.stringify(config)], { type: 'application/json' }));
        return post('/api/v1/certificates', formData);
      },
    },

    serviceDiscovery: {
      getFoundServices: (): Promise<IRegisteredServiceInfo[]> => get('/api/v1/service_discovery/registered_services'),
      removeService: (id: string): Promise<any> => destroy(`/api/v1/service_discovery/${id}`)
    },

    analytics: {
      getModels: (): Promise<IAnalyticsModelDefinition[]> => get('/api/v1/analytics/models')
    },

    systemScripts: {
      getSystemScriptTypes: (): Promise<string[]> => get('/api/v1/system/script_types'),
      getSystemScripts: (): Promise<ISystemScriptEntity[]> => get('/api/v1/system/scripts'),
      saveSystemScript: (script: ISystemScriptEntity): Promise<void> => post('/api/v1/system/scripts', script),
      testSystemScript: (script: ITestScriptSource): Promise<{ output: string; results: any; }> =>
        post('/api/v1/system/scripts/test', script),

      secrets: {
        getSecrets: (): Promise<IScriptingSecret[]> => get('/api/v1/system/scripts/secrets'),
        saveSecrets: (secret: IScriptingSecret) => post('/api/v1/system/scripts/secrets', secret),
        deleteSecret: (id: number) => destroy(`/api/v1/system/scripts/secrets/${id}`)
      },

      libraries: {
        getLibraries: () => get<{ library: IScriptingUserLibrary, loaded: boolean }[]>('/api/v1/system/scripting_user_libraries/libraries'),
        saveLibraries: (library: IScriptingUserLibrary) => post('/api/v1/system/scripting_user_libraries/libraries', library),
        deleteLibraries: (id: number) => destroy(`/api/v1/system/scripting_user_libraries/libraries/${id}`),
      }
    },

    roles: {
      getRawHierarchy: (): Promise<RawRoleHierarchy> => get('/api/v1/config/rawHierarchy'),
    },

    email: {
      testEmail: (data: ITestMail): Promise<void> => post('/api/v1/admin/test_email', data),
      sendTestEmail: (data: IEmailerController$ITestEmail) => post('/api/v1/emailer/send_test_email', data),
      downloadTestEmail: (data: IEmailerController$ITestEmail) => post('/api/v1/emailer/download_test_email', data),
    },

    nativeLibraries: {
      tesseractSetup: (data: INativeLibrarySettings): Promise<void> => post('/api/v1/admin/initialize_tesseract', data),
      libpostalSetup: (data: INativeLibrarySettings) => post('/api/v1/admin/initialize_libpostal', data),
    },

    ldap: {
      testLdapLogin: (ldapTest: ILdapTestRequest): Promise<void> => post('/api/v1/auth/admin/test_ldap', ldapTest),
    },

    ip: {
      testIp: (data: IIpFilterTestRequest): Promise<{ rule: IpFilterRule }> => post('/api/v1/auth/test_ip', data),

      ipBans: {
        getBanned: () => get<{[k: string]: any}>('/api/v1/auth/admin/blocked-ip-list'),
        reAllowIp: (ip: string) => post('/api/v1/auth/admin/blocked-ip-list/unblock', { ip })
      },
    },

    language: {
      updateLanguage: (data: IUpdateTranslations): Promise<void> =>
        post('/api/v1/translations/translationManager/update', data),
    },

    labels: {
      createLabelForSpec: (data: ILabelSpec): Promise<string[]> => post('/api/v1/label_generator/create_label', data),
      printLabel: (printer: number, data: ILabelSpec): Promise<any> => post('/api/v1/label_generator/print_label', data, { printer }),
    
      templates: {
        getAll: () => get<ILabelTemplate[]>('/api/v1/label_generator/templates'),
        save: (data: ILabelTemplate) => post<ILabelTemplate>('/api/v1/label_generator/templates', data),
        deleteById: (id: number) => destroy('/api/v1/label_generator/templates/'+id),
        runById: (id: number, printer: number, input: ILabelInput) => post<ILabelTemplate>('/api/v1/label_generator/templates/'+id+"/run", input, { printer }),
      }
    },

    system: {
      restart: () => post('/api/v1/admin/restartApplication', {}),

      testCron: (cron: string): Promise<{ next: any; nextXMoments: any[]; }> => post("/api/v1/system/test_cron", { expr: cron }),

      getAutoPicklistGeneratorSettings: (): Promise<IPicklistGenerator$IAuto> =>
        get('/api/v1/picklistGeneratorManager/picklist_generator_config'),
      saveAutoPicklistGeneratorSettings: (newSettings: IPicklistGenerator$IAuto): Promise<void> =>
        post('/api/v1/picklistGeneratorManager/picklist_generator_config', newSettings),

      getExactOnlineTokenData: () => post<any>('/api/v1/exact_online_token_data', {}),
      getReportConfigurationForSettings: (settings: ISettings) => post<IReportSettings>('/api/v1/report_configuration', settings),
      isConfigured: () => post<{setup_completed: boolean}>('/api/v1/has_setup_been_performed', {}),
      markConfigured: () => post('/api/v1/setup_completed', {}),

      getQzConfig: () => get<IQzTraySettings>('/api/v1/system/qztray_config'),
      getCoturn: () => get<{config: string}>('/api/v1/system/be_efficient_coturn'),
      getDatabaseSchema: () => post<IIWhsPerformanceDao$ISchema[]>('/api/v1/system/database_schema', {}),

      hw: {
        getThreads: () => get<{ id: number, name: string, info: string, state: string, stack: string[] }[]>(`/api/v1/system/hw/threads`), 
        getStorage: () => get<{ free: number; total: number; }>('/api/v1/system/hw/storage'),
        interrupt: (id: number) => post(`/api/v1/system/hw/thread/${id}/interrupt`, {})
      },

      serviceDiscovery: {
        start: () => post('/api/v1/jmdns/init', {}),
        stop: () => post('/api/v1/jmdns/destroy', {}),
        getCurrent: () => get<{ currentFocus: string; running: boolean; found: {[k:string]: {name: string; descriptor: string;}[]}}>('/api/v1/jmdns/getStatus'),
        getServiceInfo: (type: string, name: string) => post('/api/v1/jmdns/getServiceInfo', { type, name }),
      },

      customizer: {
        getSourceFiles: () => get<ICustomizerFile[]>('/api/v1/system/customizer/files'),
        saveSourceFile: (f: ICustomizerFile) => post<ICustomizerFile>('/api/v1/system/customizer/files', f),
        deleteSourceFile: (id: string) => destroy('/api/v1/system/customizer/files/'+id),
        saveBundle: (bundles: ICustomizerController$ICompanion$IFrontendBundle[]) => post('/api/v1/system/customizer/frontend/save_bundle', bundles)
      }
    },

    tasks: {
      scripted: {
        getAll: (): Promise<IScriptedTask[]> => get('/api/v1/tasks/scripted'),
        save: (script: IScriptedTask): Promise<any> => post('/api/v1/tasks/scripted', script),
        delete: (id: number): Promise<any> => destroy(`/api/v1/tasks/scripted/${id}`),
      },
      getTasks: (): Promise<ITaskOverview[]> => get('/api/v1/tasks/'),
      startTask: (taskId: number): Promise<void> => post(`/api/v1/tasks/${taskId}/start`, {}),
      setTaskConfig: (taskId: number, config: ITaskConfiguration): Promise<void> => post(`/api/v1/tasks/${taskId}/config`, config),
    },

    reports: {
      getAll: (): Promise<{
        report: IReportDefinition,
        nextRun: any
      }[]> => get("/api/v1/reporting/"),
      save: (def: IReportDefinition): Promise<IReportDefinition> => post("/api/v1/reporting/", def),
      delete: (id: number): Promise<void> => destroy(`/api/v1/reporting/${id}`),

      run: (id: number, params: {[key: string] :string}): Promise<void> => post(`/api/v1/reporting/${id}/run`, params),
      download: (id: number, params: {[key: string] :string}): Promise<{ file: string, name: string, mediatype: string }> => post(`/api/v1/reporting/${id}/download`, params),
    },

    emailTemplates: {
      getAll: () => get<IEmailerDefinition[]>('/api/v1/emailer/'),
      save: (def: IEmailerDefinition) => post('/api/v1/emailer/', def),
      delete: (id: number) => destroy('/api/v1/emailer/' + id),
      run: (id: number, targets: IEmailerController$IEmailerTarget[]) => post<any[]>(
        `/api/v1/emailer/${id}/run`, targets
      )
    },

    documentLayouts: {
      getAll: () => get<IDocumentLayout[]>('/api/v1/documentLayout/'),
      save: (layout: IDocumentLayout) => post('/api/v1/documentLayout/', layout),
      delete: (id: number) => destroy('/api/v1/documentLayout/' + id),
      preview: (layout: IDocumentLayout) => post<{ base64: string }>('/api/v1/documentLayout/preview/', layout),
    },

    userFunctions: {
      getAll: (): Promise<{
        userFunction: IUserFunctionDefinition,
        nextRun: any
      }[]> => get("/api/v1/user_functions/"),
      save: (def: IUserFunctionDefinition): Promise<IUserFunctionDefinition> => post("/api/v1/user_functions/", def),
      delete: (id: number): Promise<void> => destroy(`/api/v1/user_functions/${id}`),
    
      test: (def: IUserFunctionDefinition) => post<{ output: string; results: any; }>('/api/v1/user_functions/test', def),

      run: (id: number, params: {[key: string] :string}): Promise<{ taskId: number; }> => post(`/api/v1/user_functions/${id}/run`, params),
    },

    userApis: {
      getAll: () => get<{ definition: IUserApiDefinition; foundApis: any[] }[]>('/api/v1/user_api/'),
      save: (o: IUserApiDefinition) => post('/api/v1/user_api/', o),
      delete: (id: number) => destroy(`/api/v1/user_api/${id}`),
    },

    alerting: {
      getAll: (): Promise<IAlertDto[]> => get("/api/v1/alerting/"),
      save: (def: IAlertDefinition): Promise<IAlertDefinition> => post("/api/v1/alerting/", def),
      subscribe: (id: number, subscribe: boolean): Promise<void> => post(`/api/v1/alerting/${id}/subscribe/${subscribe}`, {}),
      getAlerts: (id: number): Promise<IAlertRecord[]> => get(`/api/v1/alerting/${id}`),
      delete: (id: number): Promise<void> => destroy(`/api/v1/alerting/${id}`),

      run: (id: number): Promise<{ taskId: number; }> => post(`/api/v1/alerting/${id}/run`, {}),
    },


    tabula: {
      getTables: (pdf: File, coords: ITabulaController$ICompanion$IPdfCoord[]): Promise<TabulaResults[]> => {
        const formData = new FormData();
        formData.append('file', pdf);
        formData.append('coords', new Blob([JSON.stringify(coords)], { type: 'application/json' }));
        return post('/api/v1/tabula/tables', formData);
      },
    },

    proxy: {
      performCall: (call: IProxyCall) => post<IProxyCallResult>('/api/v1/proxy/', call),
    },

    externalIntegrations: {
      getAll: () => get<IExternalServiceRegistration[]>('/api/v1/external_integration/'),
      save: (v: IExternalServiceRegistration) => post<IExternalServiceRegistration>('/api/v1/external_integration/', v),
      refreshToken: (id: number) => post(`/api/v1/external_integration/${id}/refresh_token`, {}),
      delete: (id: number) => destroy(`/api/v1/external_integration/${id}`),

      createOneTimeAuthLink: (id: number) => post<{ token: string }>(`/api/v1/external_integration/${id}/generate_one_time_only_link`, {})
    },

    printers: {
      getPrinter: (id: number): Promise<IPrinterConfig> => get(`/api/v1/printer/${id}`),
      getAvailablePrinters: (): Promise<Array<IPrinterConfig>> => get('/api/v1/printer/list'),
      testPrinter: (printerConfig: IPrinterConfig): Promise<void> => post('/api/v1/printer/test', printerConfig),
      savePrinter: (printerConfig: IPrinterConfig): Promise<void> => post(`/api/v1/printer/admin/save`, printerConfig),
      deletePrinter: (id: number): Promise<void> => destroy(`/api/v1/printer/admin/${id}`),
    
      getNativePrinters: (): Promise<{
        name: string,
        supportedDocs: {
          fullName: string,
          descriptor: {
            mediaType: string,
            mimeType: string,
            mediaSubtype: string,
          },
          mediaType: string,
          mimeType: string
        }[]
      }[]> => get(`/api/v1/printer/native/list`),
      
      getCupsData: (ip: string, port: string) => post('/api/v1/printer/cups/list', { ip, port }),

      printFiles: async (printer: number, files: File[]) => {
        const formData = new FormData();
        files.forEach(file => formData.append("files", file))
        await post(`/api/v1/printer/${printer}/batch_print`, formData, null)
      },

      convertPdf2Zpl: async (s: IDoc2Zpl) => post<{ zpl: string[] }>('/api/v1/printer/pdf2zpl', s),
      convertImg2Zpl: async (s: IDoc2Zpl) => post<{ zpl: string[] }>('/api/v1/printer/img2zpl', s),
    },

    addons: {
      deliveryConfirmation: {
        certificate: {
          fowardCertificate: () => post('/api/v1/delivery_confirmation/certificates/forward/', null),
          storeCertificate: (cert: File, config: any) => {
            const formData = new FormData();
            formData.append('cert', cert);
            formData.append('config', new Blob([JSON.stringify(config)], { type: 'application/json' }));
            return post('/api/v1/delivery_confirmation/certificates/load/', formData);
          },  
        },
        settings: {
          get: (): Promise<any> => get('/api/v1/delivery_confirmation/settings/'),
          save: (settings: any): Promise<void> => post('/api/v1/delivery_confirmation/settings/', settings),
        },    
      }
    },

    userTasks: {
      getAllTasks: () => get<IUserTask[]>('/api/v1/user_tasks/all'),
      getCurrentTasks: () => get<IUserTask[]>('/api/v1/user_tasks/'),
      getTask: (id: number) => get<IUserTask>('/api/v1/user_tasks/'+id),
      saveUserTask: (b: IUserTask) => post(`/api/v1/user_tasks/`, b),
      deleteUserTask: (id: number) => destroy(`/api/v1/user_tasks/${id}`),

      claimSharedTask: (id: number) => post(`/api/v1/user_tasks/${id}/claim`, {}),
      markFinished: (id: number) => post(`/api/v1/user_tasks/${id}/finished`, {}),
      buttonPressed: (id: number, bid: string, params: any) => post(`/api/v1/user_tasks/${id}/button_pressed/${bid}`, params),

      templates: {
        get: () => get<IUserTaskTemplate[]>('/api/v1/user_tasks/templates'),
        save: (b: IUserTaskTemplate) => post(`/api/v1/user_tasks/templates`, b),
        delete: (id: number) => destroy(`/api/v1/user_tasks/templates/${id}`),  
      }
    },

    kanbanBoards: {
      getAllBoards: () => get<IKanbanBoardEntity[]>('/api/v1/kanban/boards/simple/'),
      getBoard: (id: string) => get<IKanbanBoardEntity>(`/api/v1/kanban/boards/${id}`),
      saveBoard: (board: IKanbanBoardEntity) => post(`/api/v1/kanban/boards/`, board),
      deleteBoard: (id: string) => destroy(`/api/v1/kanban/boards/${id}`),
      reorderColumns: (boardId: string, columnId: string, newIndex: number) => post(
        `/api/v1/kanban/boards/${boardId}/reorder_columns`,
        {},
        { boardId, columnId, newIndex }
      ),

      columns: {
        saveColumn: (column: IKanbanColumnEntity) => post(
          `/api/v1/kanban/columns/`,
          column,
        ),

        deleteColumn: (id: string) => destroy(`/api/v1/kanban/columns/${id}`),

        reorderCard: (cardId: string, targetColumnId: string, newIndex: number) => post(
          `/api/v1/kanban/columns/reorder_cards`,
          {},
          { cardId, targetColumnId, newIndex }
        ),

        cards: {
          saveCard: (card: IKanbanCardEntity) => post(
            `/api/v1/kanban/cards/`,
            card
          ),

          deleteCard: (id: string) => destroy(`/api/v1/kanban/cards/${id}`),
        }
      }
    }
  };
};
